import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useStore } from '../../store';
import { useShallow } from 'zustand/react/shallow';
import { listOrgMembers } from '../../services/api';
import {
  getAgencyClinics,
  type AgencyClinicResponse,
} from '../../services/clinicsApi';
import {
  listRogerDocAccountLinks,
  buildRogerDocLinksByEmail,
  type RogerDocClinicianAccount,
} from '../../services/accountLinkApi';
import { withAgencyClinicDisplayNames } from '../../utils/agencyClinicDisplayName';
import { DEFAULT_MEMBER_STATUS_FILTER, memberStatus } from './memberStatus';
import type { MemberStatus, OrgMemberResponse } from './types';

// ─── Hook ────────────────────────────────────────────────────────────────────

export function useClinicianManager() {
  const { user, hasAnyPermission, showError } = useStore(
    useShallow(state => ({
      user: state.user,
      hasAnyPermission: state.hasAnyPermission,
      showError: state.showError,
    }))
  );

  // ── Data state ──
  const [members, setMembers] = useState<OrgMemberResponse[]>([]);
  const [clinics, setClinics] = useState<AgencyClinicResponse[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<MemberStatus[]>([
    ...DEFAULT_MEMBER_STATUS_FILTER,
  ]);

  // ── RogerDoc account links (fetched separately so the member list renders
  //    even if RogerDoc is slow/unavailable; RogerDoc is the source of truth) ──
  const [rogerDocAccounts, setRogerDocAccounts] = useState<
    RogerDocClinicianAccount[]
  >([]);
  // Whether the fetch above actually succeeded. An empty list otherwise means
  // both "nobody has an account" and "we could not ask", and the health badge
  // must not report the second as the first.
  const [rogerDocAccountsLoaded, setRogerDocAccountsLoaded] = useState(false);
  // The last fetch failed, so a "loading" spinner would never end.
  const [rogerDocAccountsFailed, setRogerDocAccountsFailed] = useState(false);

  // ── Invite dialog state ──
  const [showInviteDialog, setShowInviteDialog] = useState(false);

  // ── Data loading ──

  // The spinner is for the first load only. Later loads follow a save, and
  // swapping the list for a spinner would throw away its scroll position, its
  // sort and the selected cell.
  const hasLoadedRef = useRef(false);
  // Saves can start loads that overlap; only the latest one is shown.
  const loadSeqRef = useRef(0);
  const loadData = useCallback(async () => {
    if (!user?.agency?.id) return;
    const seq = ++loadSeqRef.current;
    if (!hasLoadedRef.current) setIsLoading(true);
    try {
      const [membersRes, clinicsData] = await Promise.all([
        listOrgMembers({}),
        getAgencyClinics(user.agency.id),
      ]);
      if (seq !== loadSeqRef.current) return;
      setMembers(membersRes.data ?? []);
      hasLoadedRef.current = true;
      // Resolved here so every clinic checkbox, badge and summary in the
      // manage and onboard dialogs has a label. A Kinnser default clinic has
      // no name of its own.
      setClinics(withAgencyClinicDisplayNames(clinicsData));
    } catch (err) {
      console.error('Failed to load clinician manager data:', err);
      showError('Failed to load organization members');
    } finally {
      setIsLoading(false);
    }
  }, [user?.agency?.id, showError]);

  // Only the newest load may write; a list handed over by the grid's App
  // accounts dialog counts as newer than any load still in flight.
  const rogerDocLinksSeqRef = useRef(0);
  const loadRogerDocLinks = useCallback(async () => {
    if (!user?.agency?.id) return;
    const seq = ++rogerDocLinksSeqRef.current;
    try {
      const accounts = await listRogerDocAccountLinks();
      if (seq !== rogerDocLinksSeqRef.current) return;
      setRogerDocAccounts(accounts);
      setRogerDocAccountsLoaded(true);
      setRogerDocAccountsFailed(false);
    } catch (err) {
      if (seq !== rogerDocLinksSeqRef.current) return;
      // Non-fatal: the member list stays usable without link info.
      setRogerDocAccountsLoaded(false);
      setRogerDocAccountsFailed(true);
      console.error('Failed to load RogerDoc account links:', err);
    }
  }, [user?.agency?.id]);
  const replaceRogerDocAccounts = useCallback(
    (accounts: RogerDocClinicianAccount[]) => {
      rogerDocLinksSeqRef.current += 1;
      setRogerDocAccounts(accounts);
      setRogerDocAccountsLoaded(true);
      setRogerDocAccountsFailed(false);
    },
    []
  );

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Separate, non-blocking fetch (per ticket: a separate call after FE loads).
  useEffect(() => {
    loadRogerDocLinks();
  }, [loadRogerDocLinks]);

  const rogerDocLinksByEmail = useMemo(
    () => buildRogerDocLinksByEmail(rogerDocAccounts, user?.agency?.id),
    [rogerDocAccounts, user?.agency?.id]
  );

  // EMR-375: every clinic is Roger-assignable (including Kinnser-participating).
  const roleAssignableClinics = clinics;

  // The member payload carries its own copy of each clinic name, so the table
  // badges need this to reach the same resolved names as the dialogs.
  const clinicNamesById = useMemo(
    () => new Map(clinics.map(clinic => [clinic.id, clinic.name])),
    [clinics]
  );

  // ── Filtered members (memoized) ──

  const { filteredMembers, showingOutsideFilter } = useMemo(() => {
    const tokens = searchQuery.toLowerCase().split(/\s+/).filter(Boolean);

    // Matched against one string so a query spanning two fields -- a full
    // name, most often -- can still match; every token has to land somewhere,
    // so extra words narrow rather than widen.
    const matchesSearch = (m: OrgMemberResponse) => {
      const haystack = [m.first_name, m.last_name, m.email, m.phone_number]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return tokens.every(token => haystack.includes(token));
    };

    const searched =
      tokens.length > 0 ? members.filter(matchesSearch) : members;
    const scoped = searched.filter(m => statusFilter.includes(memberStatus(m)));

    // The list opens without the outstanding invitations, so somebody
    // searching for a colleague who has not accepted theirs yet would not see
    // them, and nothing would say a filter is the reason. Whenever a search
    // matches anyone outside the checked statuses, show them and say so.
    if (tokens.length > 0 && searched.length > scoped.length) {
      return { filteredMembers: searched, showingOutsideFilter: true };
    }
    return { filteredMembers: scoped, showingOutsideFilter: false };
  }, [members, searchQuery, statusFilter]);

  // ── Permission check ──

  const hasAccess = hasAnyPermission(['clinician-mgr:access', 'emr:manage']);

  return {
    // Permission
    hasAccess,

    // Data
    members,
    clinics,
    roleAssignableClinics,
    clinicNamesById,
    filteredMembers,
    showingOutsideFilter,
    isLoading,
    searchQuery,
    setSearchQuery,
    statusFilter,
    setStatusFilter,
    loadData,

    // RogerDoc account links
    rogerDocAccounts,
    rogerDocAccountsLoaded,
    rogerDocAccountsFailed,
    rogerDocLinksByEmail,
    loadRogerDocLinks,
    replaceRogerDocAccounts,

    // Invite dialog
    showInviteDialog,
    openInviteDialog: () => setShowInviteDialog(true),
    closeInviteDialog: () => setShowInviteDialog(false),
  };
}
